// src/pages/StatsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Calendar, Download, Eye, Edit2, History, ChevronLeft, ChevronRight, 
  Users, DollarSign, TrendingUp, ShoppingBag, Loader2, Info,
  X, Filter, FileText, Mail, User, Clock, MapPin, GitBranch, Activity
} from 'lucide-react';
import { getBookingStats, getBookingDetails, exportBookingsToExcel, getQuotationHistory } from '../api/stats.service';
import { getDestinationById } from '../api/travel.service';

const StatsPage = ({ user: propUser }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(propUser);
  const [queries, setQueries] = useState([]);
  const [stats, setStats] = useState({
    total_queries: 0,
    total_revenue: 0,
    total_commission: 0,
    unique_customers: 0,
    avg_booking_value: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    from_date: '',
    to_date: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0
  });

  useEffect(() => {
    if (!user) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (err) {
          console.error('Error parsing user:', err);
        }
      }
    }
  }, [user]);

  const fetchQueries = async () => {
    setLoading(true);
    try {
      const response = await getBookingStats({
        page: pagination.current_page,
        per_page: pagination.per_page,
        from_date: filters.from_date,
        to_date: filters.to_date,
        search: filters.search
      });
      
      if (response.status) {
        const rawData = response.data.bookings || [];
        
        const transformedQueries = rawData.map(booking => ({
          id: booking.id,
          booking_no: booking.booking_no,
          customer_name: booking.guest_name,
          email: booking.email,
          created_at: booking.created_at,
          updated_at: booking.updated_at,
          total_amount: Number(booking.total_amount) || 0,
          commission_amount: Number(booking.commission_amount) || 0,
          commission_percentage: Number(booking.commission_percentage) || 0,
          check_in_date: booking.check_in_date,
          total_pax: booking.total_pax,
          total_adults: booking.total_adults,
          total_children: booking.total_children,
          subtotal: Number(booking.subtotal) || 0,
          items: (booking.items || []).map(item => ({
            id: item.id,
            activity_id: item.activity_id,
            destination_id: item.destination_id,
            activity_description: item.activity_description,
            pax_type: item.pax_type,
            pax_count: item.pax_count,
            adult_price: parseFloat(item.adult_price) || 0,
            child_price: parseFloat(item.child_price) || 0,
            destination_name: item.destination_name
          }))
        }));
        
        setQueries(transformedQueries);
        
        const statsData = response.data.stats || {};
        setStats({
          total_queries: Number(statsData.total_bookings) || 0,
          total_revenue: Number(statsData.total_revenue) || 0,
          total_commission: Number(statsData.total_commission) || 0,
          unique_customers: Number(statsData.unique_customers) || 0,
          avg_booking_value: Number(statsData.avg_booking_value) || 0
        });
        
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          total_pages: response.data.pagination?.total_pages || 0
        }));
      }
    } catch (err) {
      console.error('Error fetching queries:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueries();
  }, [pagination.current_page, pagination.per_page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setTimeout(() => fetchQueries(), 100);
  };

  const resetFilters = () => {
    setFilters({ from_date: '', to_date: '', search: '' });
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setTimeout(() => fetchQueries(), 100);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportBookingsToExcel(filters);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  // 🔥 FIXED: handleEdit with dynamic country fetching
  const handleEdit = async (query) => {
    console.log('✏️ Edit clicked for:', query);
    
    if (!query.items || query.items.length === 0) {
      alert('No activities found in this quotation');
      return;
    }
    
    try {
      const firstItem = query.items[0];
      const destinationId = firstItem.destination_id;
      
      // 🔥 DYNAMIC: Fetch country info from destination
      let countryId = 1; // default
      let countryName = 'Thailand';
      
      try {
        const destResponse = await getDestinationById(destinationId);
        if (destResponse.status && destResponse.data) {
          countryId = destResponse.data.country_id;
          countryName = destResponse.data.country_name;
          console.log(`🌍 Found country: ${countryName} (${countryId}) for destination ${destinationId}`);
        }
      } catch (err) {
        console.warn('Could not fetch country, using default:', err);
      }
      
      const itemsWithCorrectIds = query.items.map((item) => ({
        id: item.id,
        activity_id: item.activity_id,
        destination_id: item.destination_id,
        destination_name: item.destination_name || '',
        description: item.activity_description || '',
        type: item.pax_type || 'SIC',
        pax_count: item.pax_count || 2,
        adult_price: item.adult_price || 0,
        child_price: item.child_price || 0,
        country_id: countryId,
        country_name: countryName
      }));
      
      const editData = {
        id: query.id,
        booking_no: query.booking_no,
        guest_name: query.customer_name,
        email: query.email,
        check_in_date: query.check_in_date,
        total_pax: query.total_pax,
        children_count: query.total_children || 0,
        country_id: countryId,
        country_name: countryName,
        items: itemsWithCorrectIds
      };
      
      console.log('✅ Edit data prepared with country:', { countryId, countryName });
      
      localStorage.setItem('edit_quotation', JSON.stringify(editData));
      localStorage.setItem('edit_mode', 'true');
      
      navigate('/dashboard?edit=true');
    } catch (err) {
      console.error('Error preparing edit data:', err);
      alert('Failed to load edit data');
    }
  };

  const viewQueryDetails = async (queryId) => {
    try {
      const response = await getBookingDetails(queryId);
      if (response.status) {
        const bookingData = response.data;
        
        const transformedQuery = {
          id: bookingData.id,
          booking_no: bookingData.booking_no,
          customer_name: bookingData.guest_name,
          email: bookingData.email,
          phone: bookingData.phone || '',
          check_in_date: bookingData.check_in_date,
          check_out_date: bookingData.check_out_date,
          created_at: bookingData.created_at,
          updated_at: bookingData.updated_at,
          total_adults: bookingData.total_adults,
          total_children: bookingData.total_children,
          total_pax: bookingData.total_pax,
          subtotal: Number(bookingData.subtotal) || 0,
          commission_percentage: Number(bookingData.commission_percentage) || 0,
          commission_amount: Number(bookingData.commission_amount) || 0,
          total_amount: Number(bookingData.total_amount) || 0,
          items: (bookingData.items || []).map(item => ({
            id: item.id,
            activity_id: item.activity_id,
            destination_id: item.destination_id,
            activity_description: item.activity_description,
            pax_type: item.pax_type,
            pax_count: item.pax_count,
            adult_price: parseFloat(item.adult_price) || 0,
            child_price: parseFloat(item.child_price) || 0,
            destination_name: item.destination_name
          }))
        };
        
        setSelectedQuery(transformedQuery);
        setShowDetailsModal(true);
      }
    } catch (err) {
      console.error('Error fetching query details:', err);
    }
  };

  const viewHistory = async (queryId) => {
    setHistoryLoading(true);
    try {
      const response = await getQuotationHistory(queryId);
      if (response.status) {
        setHistoryData(response.data);
        setShowHistoryModal(true);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatItemsList = (itemsData) => {
    if (!itemsData) return 'No items';
    try {
      let items = itemsData;
      if (typeof itemsData === 'string') {
        items = JSON.parse(itemsData);
      }
      if (!items || items.length === 0) return 'No items';
      return items.map(item => {
        const desc = item.activity_description || item.description || 'Unknown';
        const type = item.pax_type || item.type || 'SIC';
        const pax = item.pax_count ? ` (${item.pax_count}P)` : '';
        return `${desc} [${type}${pax}]`;
      }).join(', ');
    } catch (e) {
      console.error('Error parsing items:', e);
      return 'Invalid data';
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => {
    const displayValue = typeof value === 'number' ? value : Number(value) || 0;
    
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">
              {title.includes('Revenue') || title.includes('Commission') || title.includes('Avg') 
                ? `${displayValue.toFixed(2)} THB` 
                : displayValue}
            </p>
          </div>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${color}`}>
            <Icon size={20} className="text-white" />
          </div>
        </div>
      </div>
    );
  };

  // Details Modal
  const QueryDetailsModal = () => {
    if (!selectedQuery) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white">
            <div className="flex items-center gap-2">
              <FileText size={20} className="text-amber-500" />
              <h3 className="font-bold text-gray-800">Quotation Details</h3>
            </div>
            <button onClick={() => setShowDetailsModal(false)} className="p-1 hover:bg-gray-100 rounded-full">
              <X size={20} />
            </button>
          </div>
          
          <div className="p-5 space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-500">Quotation No.</p><p className="font-semibold text-gray-800 text-lg">{selectedQuery.booking_no}</p></div>
                <div><p className="text-xs text-gray-500">Created</p><p className="text-gray-800">{formatDateTime(selectedQuery.created_at)}</p></div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <User size={16} className="text-amber-500" /> Guest Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-500">Guest Name</p><p className="text-gray-800 font-medium">{selectedQuery.customer_name}</p></div>
                <div><p className="text-xs text-gray-500">Email</p><p className="text-gray-800">{selectedQuery.email}</p></div>
                <div><p className="text-xs text-gray-500">Check-in Date</p><p className="text-gray-800">{formatDate(selectedQuery.check_in_date)}</p></div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Users size={16} className="text-amber-500" /> Travelers
              </h4>
              <div className="flex gap-8">
                <div className="text-center"><p className="text-xs text-gray-500">Adults</p><p className="text-2xl font-bold text-gray-800">{selectedQuery.total_adults}</p></div>
                <div className="text-center"><p className="text-xs text-gray-500">Children</p><p className="text-2xl font-bold text-gray-800">{selectedQuery.total_children}</p></div>
                <div className="text-center"><p className="text-xs text-gray-500">Total PAX</p><p className="text-2xl font-bold text-amber-600">{selectedQuery.total_pax}</p></div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Activity size={16} className="text-amber-500" /> Activities ({selectedQuery.items?.length || 0})
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {selectedQuery.items && selectedQuery.items.length > 0 ? (
                  selectedQuery.items.map((item, idx) => (
                    <div key={idx} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800">{item.activity_description}</p>
                          <div className="flex gap-3 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><MapPin size={12} />{item.destination_name || 'N/A'}</span>
                            <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{item.pax_type} {item.pax_type === 'PVT' ? `(${item.pax_count}P)` : ''}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Adult: {item.adult_price.toFixed(2)} THB</p>
                          {item.child_price > 0 && <p className="text-xs text-gray-500">Child: {item.child_price.toFixed(2)} THB</p>}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500"><Activity size={24} className="mx-auto mb-2 text-gray-300" /><p className="text-sm">No activities found</p></div>
                )}
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><DollarSign size={16} className="text-amber-500" /> Pricing</h4>
              <div className="space-y-2 bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-medium">{selectedQuery.subtotal.toFixed(2)} THB</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Commission ({selectedQuery.commission_percentage}%)</span><span className="font-medium text-amber-600">+ {selectedQuery.commission_amount.toFixed(2)} THB</span></div>
                <div className="flex justify-between border-t pt-2 mt-2"><span className="font-bold text-gray-800">Total Amount</span><span className="font-bold text-green-600 text-lg">{selectedQuery.total_amount.toFixed(2)} THB</span></div>
              </div>
            </div>
            
            <div className="border-t pt-4 flex gap-3">
              <button onClick={() => { setShowDetailsModal(false); viewHistory(selectedQuery.id); }} className="flex-1 bg-purple-500 text-white py-2.5 rounded-lg font-medium hover:bg-purple-600 flex items-center justify-center gap-2"><History size={18} /> View Version History</button>
              <button onClick={() => { setShowDetailsModal(false); handleEdit(selectedQuery); }} className="flex-1 bg-amber-500 text-white py-2.5 rounded-lg font-medium hover:bg-amber-600 flex items-center justify-center gap-2"><Edit2 size={18} /> Edit Quotation</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // History Modal
  const HistoryModal = () => {
    if (!historyData) return null;
    const { current, history } = historyData;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white">
            <div className="flex items-center gap-2"><GitBranch size={20} className="text-amber-500" /><h3 className="font-bold text-gray-800">Quotation Version History</h3><span className="text-xs text-gray-500 ml-2">{current?.booking_no}</span></div>
            <button onClick={() => setShowHistoryModal(false)} className="p-1 hover:bg-gray-100 rounded-full"><X size={20} /></button>
          </div>
          
          <div className="p-5">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3"><div className="w-2 h-2 bg-green-500 rounded-full"></div><h4 className="font-semibold text-gray-800">Current Version</h4><span className="text-xs text-gray-500">(Latest)</span></div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-gray-500">Guest:</span> <strong>{current?.guest_name}</strong></div>
                  <div><span className="text-gray-500">Email:</span> {current?.email}</div>
                  <div><span className="text-gray-500">Check-in:</span> {formatDate(current?.check_in_date)}</div>
                  <div><span className="text-gray-500">PAX:</span> {current?.total_pax} (A:{current?.total_adults}, C:{current?.total_children})</div>
                </div>
                <div className="mt-2 pt-2 border-t border-green-200"><p className="text-xs text-gray-600 font-medium mb-1">Activities:</p><p className="text-xs text-gray-700">{formatItemsList(current?.items)}</p></div>
              </div>
            </div>
            
            {history && history.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3"><div className="w-2 h-2 bg-amber-500 rounded-full"></div><h4 className="font-semibold text-gray-800">Previous Versions</h4><span className="text-xs text-gray-500">({history.length} versions)</span></div>
                <div className="space-y-3">
                  {history.map((version) => (
                    <div key={version.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2"><span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-semibold">Version {version.version_number}</span><span className="text-xs text-gray-500">Updated: {formatDateTime(version.updated_at)}</span></div>
                        <span className="text-xs text-gray-400">By: {version.updated_by_name || 'System'}</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-gray-500">Guest:</span> {version.guest_name}</div>
                        <div><span className="text-gray-500">Email:</span> {version.email}</div>
                        <div><span className="text-gray-500">Check-in:</span> {formatDate(version.check_in_date)}</div>
                        <div><span className="text-gray-500">PAX:</span> {version.total_pax} (A:{version.total_adults}, C:{version.total_children})</div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-200"><p className="text-xs text-gray-600 font-medium mb-1">Activities:</p><p className="text-xs text-gray-700">{formatItemsList(version.items_data)}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6"><h1 className="text-2xl font-bold text-gray-800">Quotation Statistics</h1><p className="text-gray-500 text-sm mt-1">Your quotation history and reports</p></div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <StatCard title="Total Quotations" value={stats.total_queries} icon={ShoppingBag} color="bg-blue-500" />
          <StatCard title="Total Revenue" value={stats.total_revenue} icon={DollarSign} color="bg-green-500" />
          <StatCard title="Total Commission" value={stats.total_commission} icon={TrendingUp} color="bg-purple-500" />
          <StatCard title="Unique Customers" value={stats.unique_customers} icon={Users} color="bg-amber-500" />
          <StatCard title="Avg Quotation Value" value={stats.avg_booking_value} icon={Info} color="bg-indigo-500" />
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[150px]"><label className="text-xs text-gray-500 block mb-1">From Date</label><input type="date" name="from_date" value={filters.from_date} onChange={handleFilterChange} className="w-full p-2 border border-gray-200 rounded-lg text-sm" /></div>
            <div className="flex-1 min-w-[150px]"><label className="text-xs text-gray-500 block mb-1">To Date</label><input type="date" name="to_date" value={filters.to_date} onChange={handleFilterChange} className="w-full p-2 border border-gray-200 rounded-lg text-sm" /></div>
            <div className="flex-1 min-w-[200px]"><label className="text-xs text-gray-500 block mb-1">Search</label><div className="relative"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input type="text" name="search" placeholder="Name, Email, Quotation No..." value={filters.search} onChange={handleFilterChange} className="w-full pl-9 p-2 border border-gray-200 rounded-lg text-sm" /></div></div>
            <div className="flex gap-2"><button onClick={applyFilters} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"><Filter size={14} className="inline mr-1" /> Apply</button><button onClick={resetFilters} className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50">Reset</button><button onClick={handleExport} disabled={exporting} className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50">{exporting ? <Loader2 size={14} className="animate-spin inline" /> : <Download size={14} className="inline" />} Export</button></div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-amber-500" size={32} /></div> : queries.length === 0 ? <div className="text-center py-12"><Calendar size={28} className="text-gray-400 mx-auto mb-2" /><p className="text-gray-500">No quotations found</p></div> : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100"><tr><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quotation No.</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guest Name</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-in</th><th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">PAX</th><th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th><th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th></tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {queries.map((query) => (
                    <tr key={query.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3"><span className="text-sm font-mono font-medium text-gray-800">{query.booking_no}</span>{query.updated_at && query.updated_at !== query.created_at && <span className="ml-2 text-xs text-amber-500">(Updated)</span>}</td>
                      <td className="px-4 py-3"><div className="flex items-center gap-2"><User size={14} className="text-gray-400" /><span className="text-sm text-gray-700">{query.customer_name}</span></div></td>
                      <td className="px-4 py-3"><div className="flex items-center gap-2"><Mail size={14} className="text-gray-400" /><span className="text-sm text-gray-500">{query.email}</span></div></td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(query.check_in_date)}</td>
                      <td className="px-4 py-3 text-center"><span className="inline-flex items-center justify-center w-8 h-8 bg-amber-50 text-amber-600 rounded-full text-sm font-medium">{query.total_pax}</span></td>
                      <td className="px-4 py-3 text-right"><span className="text-sm font-semibold text-green-600">{query.total_amount.toFixed(2)} THB</span></td>
                      <td className="px-4 py-3 text-sm text-gray-400">{formatDate(query.created_at)}</td>
                      <td className="px-4 py-3 text-center"><div className="flex items-center justify-center gap-2"><button onClick={() => viewQueryDetails(query.id)} className="p-2 hover:bg-blue-50 rounded-lg"><Eye size={18} className="text-blue-500" /></button><button onClick={() => viewHistory(query.id)} className="p-2 hover:bg-purple-50 rounded-lg"><History size={18} className="text-purple-500" /></button><button onClick={() => handleEdit(query)} className="p-2 hover:bg-amber-50 rounded-lg"><Edit2 size={18} className="text-amber-500" /></button></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          
          {pagination.total_pages > 1 && (
            <div className="px-4 py-3 border-t border-gray-100 flex justify-between items-center">
              <div className="text-xs text-gray-500">Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}</div>
              <div className="flex gap-2"><button onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))} disabled={pagination.current_page === 1} className="px-3 py-1.5 rounded-lg text-sm bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"><ChevronLeft size={14} /> Previous</button><span className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium">{pagination.current_page}</span><button onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))} disabled={pagination.current_page === pagination.total_pages} className="px-3 py-1.5 rounded-lg text-sm bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50">Next <ChevronRight size={14} /></button></div>
            </div>
          )}
        </div>
      </div>
      
      {showDetailsModal && <QueryDetailsModal />}
      {showHistoryModal && <HistoryModal />}
    </div>
  );
};

export default StatsPage;